import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Globe, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import allLangsImport from "pages/TranslatesPage/allLangs"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const allowedLanguageCodes = ["en", "pt", "lt", "es", "fr", "it", "de", "ar", "he"]
const allLangs = allLangsImport.filter((lang) => allowedLanguageCodes.includes(lang.code))

export interface ProductTranslation {
  code: string
  name: string
}

export interface SKUTranslation {
  code: string
  name: string
}

export interface ProductTranslationsData {
  productName: ProductTranslation[]
  skuNames: Record<string, SKUTranslation[]>
}

interface ProductTranslationsProps {
  productId: string
  skuIds: string[]
  defaultProductName: string
  defaultSkuNames: Record<string, string>
  defaultSkuNumbers?: Record<string, string>
  translations: ProductTranslationsData
  onChange: (translations: ProductTranslationsData) => void
  savedLanguages?: string[]
}

export const ProductTranslations: React.FC<ProductTranslationsProps> = ({
  productId: _productId,
  skuIds,
  defaultProductName,
  defaultSkuNames,
  defaultSkuNumbers,
  translations,
  onChange,
  savedLanguages = [],
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    const existingSkuIds = Object.keys(translations.skuNames)
    const missingSkuIds = skuIds.filter((skuId) => !existingSkuIds.includes(skuId))

    if (missingSkuIds.length > 0 && translations.productName.length > 0) {
      const updatedSkuNames = { ...translations.skuNames }

      missingSkuIds.forEach((skuId) => {
        updatedSkuNames[skuId] = translations.productName.map((t) => ({
          code: t.code,
          name: "",
        }))
      })

      onChange({
        ...translations,
        skuNames: updatedSkuNames,
      })
    }
  }, [skuIds.join(","), translations.productName.length])

  const availableLangs = allLangs.filter(
    (lang) => !translations.productName.some((t) => t.code === lang.code)
  )

  const handleAddLanguage = async (langCode: string) => {
    const lang = allLangs.find((l) => l.code === langCode)
    if (!lang) return

    const newProductTranslation: ProductTranslation = {
      code: langCode,
      name: "",
    }

    const newSkuTranslations: Record<string, SKUTranslation[]> = {}
    skuIds.forEach((skuId) => {
      newSkuTranslations[skuId] = [
        ...(translations.skuNames[skuId] || []),
        { code: langCode, name: "" },
      ]
    })

    const updated = {
      productName: [...translations.productName, newProductTranslation],
      skuNames: { ...translations.skuNames, ...newSkuTranslations },
    }

    onChange(updated)
  }

  const handleRemoveLanguage = (langCode: string) => {
    onChange({
      productName: translations.productName.filter((t) => t.code !== langCode),
      skuNames: Object.keys(translations.skuNames).reduce((acc, skuId) => {
        acc[skuId] = (translations.skuNames[skuId] || []).filter((t) => t.code !== langCode)
        return acc
      }, {} as Record<string, SKUTranslation[]>),
    })
  }

  const handleProductNameChange = (langCode: string, value: string) => {
    const updated = {
      ...translations,
      productName: translations.productName.map((t) =>
        t.code === langCode ? { ...t, name: value } : t
      ),
    }
    onChange(updated)
  }

  const handleSkuNameChange = (skuId: string, langCode: string, value: string) => {
    const updated = {
      ...translations,
      skuNames: {
        ...translations.skuNames,
        [skuId]: (translations.skuNames[skuId] || []).map((t) =>
          t.code === langCode ? { ...t, name: value } : t
        ),
      },
    }
    onChange(updated)
  }

  return (
    <div className="space-y-3 py-1">
      {/* Default language card */}
      <Card className="bg-muted/50">
        <CardContent className="pt-4">
          <div className="mb-3 flex items-center gap-2">
            <Badge variant="secondary">Default</Badge>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("productPage.productNameLabel")}</Label>
              <Input value={defaultProductName} placeholder={defaultProductName} disabled />
            </div>
            {skuIds.length > 0 &&
              skuIds.map((skuId) => {
                const defaultSkuName = defaultSkuNames[skuId] || ""
                const skuNumber = defaultSkuNumbers?.[skuId] || ""
                const variantLabel = skuNumber ? `Variant — SKU: ${skuNumber}` : "Variant"
                return (
                  <div key={skuId} className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{variantLabel}</Label>
                    <Input value={defaultSkuName} placeholder={defaultSkuName} disabled />
                  </div>
                )
              })}
          </div>
        </CardContent>
      </Card>

      {/* Translation cards */}
      {translations.productName.map((translation) => {
        const lang = allLangs.find((l) => l.code === translation.code)
        const isSaved = savedLanguages.includes(translation.code)
        return (
          <Card key={translation.code}>
            <CardContent className="pt-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">{lang?.lang || translation.code}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {translation.code.toUpperCase()}
                  </Badge>
                </div>
                {!isSaved && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemoveLanguage(translation.code)}
                  >
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Remove
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("productPage.productNameLabel")}</Label>
                  <Input
                    name={`product_name_${translation.code}`}
                    onChange={(e) => handleProductNameChange(translation.code, e.target.value)}
                    value={translation.name}
                    placeholder={defaultProductName}
                  />
                </div>
                {skuIds.length > 0 &&
                  skuIds.map((skuId) => {
                    const skuTranslation = translations.skuNames[skuId]?.find(
                      (t) => t.code === translation.code
                    )
                    const defaultSkuName = defaultSkuNames[skuId] || ""
                    const skuNumber = defaultSkuNumbers?.[skuId] || ""
                    const variantLabel = skuNumber ? `Variant — SKU: ${skuNumber}` : "Variant"
                    return (
                      <div key={skuId} className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">{variantLabel}</Label>
                        <Input
                          name={`sku_name_${skuId}_${translation.code}`}
                          onChange={(e) =>
                            handleSkuNameChange(skuId, translation.code, e.target.value)
                          }
                          value={skuTranslation?.name || ""}
                          placeholder={defaultSkuName}
                        />
                      </div>
                    )
                  })}
              </div>
            </CardContent>
          </Card>
        )
      })}

      {/* Add translation button */}
      {availableLangs.length > 0 && (
        <>
          <Separator />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                {t("productPage.addTranslation")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              side="top"
              className="w-[var(--radix-dropdown-menu-trigger-width)]"
            >
              {availableLangs.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  className="cursor-pointer"
                  onClick={() => handleAddLanguage(lang.code)}
                >
                  <Globe className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                  {lang.lang}
                  <span className="ml-auto text-xs text-muted-foreground">{lang.code}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  )
}
